import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { LaasOption } from 'src/app/core/models/laas-option';
import { deepCopy, getDataScopeByPermission, getForwarderByPermission, isNullOrEmpty, laaSFormatString } from 'src/app/core/utils/common-functions';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { WarehousingRegistrationService } from '../../services/warehousing-registration.service';
import { FacilityService } from 'src/app/core/services/facility.service';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { DataTableSavedBean } from 'src/app/shared/components/data-table/bean/data-table-saved-bean';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { SearchWhRegistrationResults } from 'src/app/core/models/transport-operation/search-whRegistration-results';
import { UserType } from 'src/app/core/enums/user-type.enum';
import { DataScope } from 'src/app/core/models/data-scope';
import { FacilityOptions } from 'src/app/core/models/options/facility-options';

@Component({
  selector: 'laas-registration-form-search',
  templateUrl: './registration-form-search.component.html',
  styleUrls: ['./registration-form-search.component.scss']
})
export class RegistrationFormSearchComponent extends SessionBaseComponent implements OnInit {

  readonly permissionCode = PermissionConstants.ViewRegistrationFormSearch;
  readonly editPermissionCode = PermissionConstants.EditRegistrationForm;
  @ViewChild('headerTable') dataTable!: DataTableComponent;

  currentLang: string = this.laasSetting.language;
  isSearchFormOpen: boolean = true;
  searchForm: FormGroup;
  isLoading: boolean = false;
  columns: any[];
  selectedRows: any[] = [];
  searchParams: any;
  submitted: boolean = false;
  referenceTypeOptions: Array<LaasOption> = [];
  searchResults: SearchWhRegistrationResults[] = [];
  forwarderOptions: any[];
  orgOptions: any[] = [];
  isArrivedOptions: any[] = [];
  registrationKeyList: string[] = [];
  editMode: boolean = false;
  vendorSuggests: string[] = [];
  vendorOptions: LaasOption[] = [];
  canEditTable: boolean;
  cloneResult: any[] = [];
  editForwarder: boolean = true;
  abnormalOptions: any[] = [];
  dataScopes: DataScope[] = [];
  facilityOptions: Array<LaasOption> = [];

  constructor(private fb: FormBuilder, injector: Injector,
    private warehousingRegistrationService: WarehousingRegistrationService,
    private facilityService: FacilityService,
    private dropdownOptionsService: DropdownOptionsService) {
    super(injector);

    this.canEditTable = false;
    this.forwarderOptions = [];

    this.searchParams = {
      registrationKey: [],
      referenceType: null,
      referenceKey: [],
      forwarder: null,
      createDtStart: null,
      createDtEnd: null,
      isArrived: null,
      abnormalReference:[],
      dataScopes:null,
      facility:[]
    }

    this.searchForm = this.fb.group({
      registrationKey: [null],
      referenceType: [null],
      referenceKey: [{ value: null, disabled: true }],
      forwarder: [null],
      createDtStart: [null],
      createDtEnd: [null],
      isArrived: [null],
      abnormalReference:[null],
      facility:[null]
    })

    this.columns = [
      { field: 'registrationKey', header: 'RegistrationFormSearch.MainTable.Registrationkey', sort: true },
      { field: 'forwarderCode', header: 'RegistrationFormSearch.MainTable.Forwarder', sort: true },
      { field: 'vendorName', header: 'RegistrationFormSearch.MainTable.Vendor', sort: true },
      { field: 'orgId', header: 'RegistrationFormSearch.MainTable.Org', sort: true },
      { field: 'referenceKey', header: 'RegistrationFormSearch.MainTable.Referencekey', sort: true },
      { field: 'referenceType', header: 'RegistrationFormSearch.MainTable.ReferenceType', sort: true },
      { field: 'edc', header: 'RegistrationFormSearch.MainTable.EDC', sort: true },
      { field: 'totalCtn', header: 'RegistrationFormSearch.MainTable.TotalCartonNo', sort: true },
      { field: 'palletCnt', header: 'RegistrationFormSearch.MainTable.PalletCnt', sort: true },
      { field: 'isArrived', header: 'RegistrationFormSearch.MainTable.IsArrived', sort: true },
      { field: 'abnormalReference', header: 'RegistrationFormSearch.MainTable.Abnormal', sort: true },
      { field: 'createUser', header: 'RegistrationFormSearch.MainTable.Creater', sort: true },
      { field: 'createDt', header: 'RegistrationFormSearch.MainTable.CreateDt', sort: true },
      { field: 'updateUser', header: 'RegistrationFormSearch.MainTable.Updater', sort: true },
      { field: 'updateDt', header: 'RegistrationFormSearch.MainTable.UpdateDt', sort: true },
    ]

    const routeData = this.routeStateService.getCurrentAndConsume()?.data;
    if (!isNullOrEmpty(routeData)) {
      this.registrationKeyList = routeData.registrationKeyList;
      this.registrationKeyList.forEach((element: any) => {
        this.searchParams.registrationKey.push(element);
      });
      this.searchForm.controls.registrationKey.setValue(routeData.registrationKeyList);
      this.searchParams.dataScopes = routeData.dataScopes;
      if (routeData.errMsgs != null && routeData.errMsgs.length > 0) {
        const msgs = routeData.errMsgs
          .reduce((str: string, msg: string, idx: number) => {
            str += `${idx + 1}. ${msg}\n`;
            return str;
          }, '');
        this.toastService.error(msgs);
      } else {
        this.searchResult();
      }
    }
  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    this.dataScopes = getDataScopeByPermission(this.userProfile, this.permissionCode);

    this.dropdownOptionsService.getForwarderOptions().subscribe({
      next: (options: any[]) => {
        this.forwarderOptions = options.filter((x: any) => {
          return ( this.userProfile.userType === UserType.SuperAdmin.toString() ||
          this.userProfile.userType === UserType.Shipping.toString() ||
          getForwarderByPermission(this.userProfile, this.permissionCode).includes(x.optionCode));
        }).map(x => ({ label: x.optionDesc, value: x.optionCode }));
      }
    });

    this.dropdownOptionsService.getLetterOrgOptions()
      .subscribe({
        next: (options: any[]) => {
          this.orgOptions = options.map(x => ({ label: `${x.legalEntityOUName}`, value: x.legalEntityOUCode }));
        }
    });

    this.facilityService.getAllVendorOptions().subscribe({
      next: (options) => {

        for (let option of options) {

          this.orgOptions.push({
            label: `${option.optionCode} - ${option.optionDesc}`,
            value: option.optionCode
          });
        }
      }
    });

    this.warehousingRegistrationService.getAbnormalOptions("WarehousingAbnormalTaskCode")
    .subscribe({
      next:(option:any)=>{
        const abnormalList = option.appCsmSettingDetailList;
        if(this.laasSetting.language == 'zh-tw'){
          this.abnormalOptions = [...abnormalList].map((x:any) =>{
            return {
              label:  `${x.setupValue}-${x.setupValueDesc}`,
              value: x.setupValue,
              source: x
            };
          })
        }else{
          this.abnormalOptions = [...abnormalList].map((x:any) =>{
            return {
              label:  `${x.setupValue}-${x.setupValue2}`,
              value: x.setupValue,
              source: x
            };
          })
        }
      }
    })

    this.isArrivedOptions = [
      { label: 'ALL', value: null },
      { label: 'Y-是', value: 'Y' },
      { label: 'N-否', value: 'N' }
    ];

    this.referenceTypeOptions = [
      { label: this.translateService.instant("RegistrationFormSearch.Search.Hawb"), value: 'HawbNo' },
      { label: this.translateService.instant("RegistrationFormSearch.Search.WhNo"), value: 'WarehousingNo' },
      { label: this.translateService.instant("RegistrationFormSearch.Search.BondedNo"), value: 'BondedNo' }];

    this.canEditTable = this.permissionCodes.includes(this.editPermissionCode);
    //貨代不能編輯貨代欄位
    if (this.userProfile.userType === UserType.Forwarder.toString()) {
      this.editForwarder = false;
    }

    this.dropdownOptionsService.getFacilityOptions().subscribe({
      next: (options: FacilityOptions[]) => {
        this.facilityOptions = options.map((x) => ({
          label: x.optionDesc,
          value: x.optionCode,
        }));
      },
    });
  }

  searchBtnClick() {
    this.dataTable.cancelRowEdit();
    this.dataTable.isInEditMode = false;
    this.dataTable.setCurrentPage(0);
    this.searchParams.dataScopes = this.dataScopes;
    this.searchResult();
  }

  searchResult(){
    this.isLoading = true;
    this.selectedRows.length = 0;
    if (isNullOrEmpty(this.searchParams.registrationKey) || this.searchParams.registrationKey.length == 0) {
      this.searchParams.registrationKey = null;
    }

    if (isNullOrEmpty(this.searchParams.referenceKey) || this.searchParams.referenceKey.length == 0) {
      this.searchParams.referenceKey = null;
    }
    this.warehousingRegistrationService.searchWarehousingRegistration(this.searchParams)
      .subscribe({
        next: result => {
          this.isSearchFormOpen = false;
          this.searchResults = [];

          //Update table
          if (!isNullOrEmpty(result)) {
            this.searchResults = deepCopy(result) ?? [];
          }

          // 日期轉換
          for (const row of this.searchResults) {
            if (!isNullOrEmpty(row.edc)) {
              row.edc = new Date(row.edc);
            }
            if (!isNullOrEmpty(row.createDt)) {
              row.createDt = new Date(row.createDt);
            }
            if (!isNullOrEmpty(row.updateDt)) {
              row.updateDt = new Date(row.updateDt);
            }
          }

          this.dataTable.updateTableData(this.searchResults);

          this.isLoading = false;
        },
        error: (err: any) => {
          this.searchResults = [];
          this.dataTable.updateTableData(this.searchResults);
          this.toastErrorMessage(err.error);
          this.isLoading = false;
        }
      });
  }

  resetBtnClick(event: any) {
    this.searchForm.reset();
    this.searchForm.controls.referenceKey.disable();
  }

  comfirmArrived(event: any) {
    this.isLoading = true;
    this.warehousingRegistrationService.comfirmArrivedWarehousing(this.selectedRows)
      .subscribe({
        next: result => {
          this.isLoading = false;
          this.toastService.success("Confirmed success");
        },
        error: (err: any) => {
          this.toastErrorMessage(err.error);
          this.isLoading = false;
        }
      })
  }

  downloadExcelBtn($event: Event) {
    this.isLoading = true;
    var whRegistrationExportRequest ;
    if (this.selectedRows.length == 0) {
      whRegistrationExportRequest = {
        req: this.searchParams,
        dataScopes: this.dataScopes,
      };
    } else {
      whRegistrationExportRequest = {
        registrationKeyList: this.selectedRows.map((obj) => obj.registrationKey),
        dataScopes: this.dataScopes
      };
    }
    this.warehousingRegistrationService.exportExcel(whRegistrationExportRequest).subscribe({
      next: result => {
        if (result == null || result.size == 0) {
          this.translateService.get('Common.Toast.NoData').subscribe({
            next: i18nResult => {
              this.toastService.info(i18nResult);
            }
          });
          this.isLoading = false;
        } else {
          const file = result;

          file.name = ("Warehousing Registration_" + new Date().toLocaleDateString() + ".xlsx");


          const url = URL.createObjectURL(file);
          let a: any = document.createElement('a');
          a.href = url
          a.download = file.name;
          a.click();
          URL.revokeObjectURL(url);
        }
      },
      complete: () => {
        this.isLoading = false;
      }
    })
  }

  saveData(savedDataBean: DataTableSavedBean): void {
    const saveDataRequest = {
      updateList: savedDataBean.updatedList
    };
    this.warehousingRegistrationService.updateRegistration(saveDataRequest).subscribe({
      next: () => {
        this.dataTable.saveSuccessEvent();
        this.searchBtnClick();
        this.toastService.success(this.translateService.instant('Common.Toast.SaveSuccess'));
      },
      error: async (response: { status: string; error: { errorMsg: string; }; }) => {
        this.isLoading = false;
        var instantMsg = await this.translateService.get("Common.Toast.SaveFail").toPromise();
        var msg = `${laaSFormatString(instantMsg, [response.status, response.error.errorMsg])}\n`;
        this.toastService.error(msg);
      }
    });
  }

  /**
   * chips add event.
   *
   * @param event p-chips onAdd event.
   * @param param
   */
  chipsAdd(event: any, param: string): void {
    const value: string = event.value.trim();
    const parsedValues: string[] = value
      .split('\r\n')
      .filter((x) => isNullOrEmpty(x) === false && x !== '\r\n');
    this.searchParams[param].splice(this.searchParams[param].indexOf(0));
    this.searchParams[param] = this.searchParams[param].concat(parsedValues);
  }

  selectReferenceType() {
    if (this.searchForm.controls.referenceType.value != null) {
      this.searchForm.controls.referenceKey.enable();
    }
  }

  dateOnChange(date: any, type: string) {
    if (type === 'createDt') {
      this.searchForm.controls.createDtStart.setValue(date.startDate);
      this.searchForm.controls.createDtEnd.setValue(date.endDate);
      this.searchParams.createDtStart = date.startDate;
      this.searchParams.createDtEnd = date.endDate;
    }
  }

  switchEditMode() {
    this.editMode = !this.editMode;
    console.log(this.editMode);
  }

  getVendorOptions(event: any): void {
    const key: string = event.query;
    if (key != null && key.length > 1) {
      this.dropdownOptionsService.getVendorOptions(key).subscribe({
        next: (options: any[]) => {
          this.vendorSuggests = options.map(x => x.optionDesc);
          this.vendorOptions = options.map(x => {
            return { label: x.optionDesc, value: x.optionCode };
          });
        }
      });
    }
  }

  dataInputValid(rowData: SearchWhRegistrationResults): boolean {
    if (rowData) {
      if (isNullOrEmpty(rowData.forwarderCode) || isNullOrEmpty(rowData.vendorName) ||
        isNullOrEmpty(rowData.orgId) || isNullOrEmpty(rowData.edc) || isNullOrEmpty(rowData.referenceKey) ||
        isNullOrEmpty(rowData.referenceType) || isNullOrEmpty(rowData.totalCtn)) {
        this.toastService.warn(this.translateService.instant('Common.Toast.CheckRequiredFields'));
        return false;
      }
    }
    return true;
  }

  optionsDataOnChange(data: any, type: string): void {
    switch (type) {
      case 'facility':
        this.searchParams.facility = data;
        break;
    }
  }
}


